import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';

import { axiosPrivate } from 'app/utils';

import type { PlusWebsitesResponse } from '../../../hooks/usePlusWebsites';

interface UpdateWebsiteDomainParams {
  websiteId: string;
  domain: string;
}

function updateWebsiteDomain({ websiteId, domain }: UpdateWebsiteDomainParams) {
  return axiosPrivate
    .put(`/v3/support/plus-websites/${websiteId}/domain`, { domain })
    .then((r) => r.data);
}

export function useUpdateWebsiteDomain() {
  const queryClient = useQueryClient();
  const queryKey = ['plus-websites'];

  return useMutation({
    mutationFn: updateWebsiteDomain,
    // Deliberately no invalidation after the write: the list endpoint can lag
    // behind this PUT, so an immediate refetch may return the old domain and
    // overwrite this cache update (and refetchOnWindowFocus is disabled, so it
    // would stick until a remount). The accepted PUT is the source of truth.
    onSuccess: (_data, { websiteId, domain }: UpdateWebsiteDomainParams) => {
      queryClient.setQueriesData<AxiosResponse<PlusWebsitesResponse>>({ queryKey }, (old) =>
        old
          ? {
              ...old,
              data: {
                ...old.data,
                plusWebsites: old.data.plusWebsites.map((website) =>
                  website.id === websiteId ? { ...website, domain } : website
                ),
              },
            }
          : old
      );
    },
    meta: {
      successMessage: 'Domain updated successfully',
      errorMessage: 'Failed to update domain',
    },
  });
}
